import { highlightRegistry } from "@/lib/highlight-registry";

/**
 * 生成中の応答を、届いたまとまりごとではなく少しずつ浮かび上がらせる。
 *
 * 受信済みの本文は Markdown として描画したまま、まだ見せない文字を CSS Custom Highlight API で
 * 透明にする。描画済みの DOM の文字を単位に進めるので、`**` などの記法が 1 文字ずつ見えることはなく、
 * Markdown の再解析もまとまりが届いたときだけで済む。色は `::highlight()` 側 (globals.css) が持つ。
 */

/** 表示が受信済みに追いつくまでの時間。 */
const CATCH_UP_MS = 150;

/** 残りが少ないときの最低速度 (文字/ms)。比例だけだと末尾の数文字がいつまでも出ない。 */
const MIN_CHARS_PER_MS = 0.1;

/** 表示位置の手前で文字を薄く残す帯の幅 (文字数)。2 段重ねて浮かび上がるように見せる。 */
const FADE_WIDTH = 8;

const HIDDEN_HIGHLIGHT = "izanagi-reveal-hidden";
const FADE_NEAR_HIGHLIGHT = "izanagi-reveal-fade-near";
const FADE_FAR_HIGHLIGHT = "izanagi-reveal-fade-far";

/** 履歴検索の一致色より上に塗り、まだ見せない文字が一致色で透けないようにする。 */
const REVEAL_PRIORITY = 1;

/** 経過時間 dtMs の後に表示している文字数。受信済みとの差に比例した速さで追う。 */
export function nextRevealLength(shown: number, target: number, dtMs: number): number {
	const rate = Math.max((target - shown) / CATCH_UP_MS, MIN_CHARS_PER_MS);
	return Math.min(target, shown + rate * Math.max(0, dtMs));
}

/**
 * 表示を進める対象のテキストノード。ブロック要素の間に入る改行だけのノードは画面に出ず、
 * 数えると表示位置がそこで止まって見えるので除く (Range は連続なので隠す範囲からは漏れない)。
 */
export function revealableTextNodes(root: Node): Text[] {
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	const nodes: Text[] = [];
	for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
		const text = node as Text;
		if (/\S/u.test(text.data)) nodes.push(text);
	}
	return nodes;
}

export function revealableText(nodes: readonly Text[]): string {
	return nodes.map((node) => node.data).join("");
}

/** 前回描いた文字と先頭から一致する文字数。ここより後ろは、届いた文字か描き直された文字。 */
export function commonPrefixLength(a: string, b: string): number {
	let length = 0;
	while (length < a.length && length < b.length && a[length] === b[length]) length++;
	return length;
}

/** 先頭から offset 文字目の位置。ノードの境目は手前のノードの末尾として返す。 */
function locate(nodes: readonly Text[], offset: number): { node: Text; offset: number } {
	let rest = Math.max(0, offset);
	for (const node of nodes) {
		if (rest <= node.data.length) return { node, offset: rest };
		rest -= node.data.length;
	}
	const last = nodes.at(-1) as Text;
	return { node: last, offset: last.data.length };
}

function rangeBetween(nodes: readonly Text[], from: number, to: number): Range {
	const start = locate(nodes, from);
	const end = locate(nodes, to);
	const range = document.createRange();
	range.setStart(start.node, start.offset);
	range.setEnd(end.node, end.offset);
	return range;
}

export interface RevealRanges {
	hidden: Range;
	fadeNear: Range;
	fadeFar: Range;
}

/** 帯が末尾を抜けきる表示位置。ここまで進めて、最後の文字も帯を通って浮かび上がらせる。 */
export function revealEnd(total: number): number {
	return total + FADE_WIDTH * 2;
}

/**
 * shown 文字目以降を隠し、その手前を 2 段で薄くする範囲。settled より手前は見せきった文字なので
 * 帯をかけ直さない (かけると、次のまとまりが届くたびに末尾が一度薄くなってちらつく)。
 * total は nodes の文字数 (revealableText の長さ)。毎フレーム数え直さないよう呼び出し側が持つ。
 * 塗るものが無ければ null。
 */
export function revealRanges(
	nodes: readonly Text[],
	total: number,
	shown: number,
	settled = 0,
): RevealRanges | null {
	const at = Math.floor(shown);
	if (Math.max(settled, at - FADE_WIDTH * 2) >= total) return null;
	const clamp = (offset: number) => Math.min(total, Math.max(settled, offset));
	return {
		hidden: rangeBetween(nodes, clamp(at), total),
		fadeNear: rangeBetween(nodes, clamp(at - FADE_WIDTH), clamp(at)),
		fadeFar: rangeBetween(nodes, clamp(at - FADE_WIDTH * 2), clamp(at - FADE_WIDTH)),
	};
}

function revealHighlight(range: Range): Highlight {
	const highlight = new Highlight(range);
	highlight.priority = REVEAL_PRIORITY;
	return highlight;
}

export function clearReveal(): void {
	const registry = highlightRegistry();
	if (registry === null) return;
	registry.delete(HIDDEN_HIGHLIGHT);
	registry.delete(FADE_NEAR_HIGHLIGHT);
	registry.delete(FADE_FAR_HIGHLIGHT);
}

/** 塗るものが残っていれば true。未対応環境では何もせず false。 */
export function paintReveal(
	nodes: readonly Text[],
	total: number,
	shown: number,
	settled: number,
): boolean {
	const registry = highlightRegistry();
	if (registry === null) return false;
	const ranges = revealRanges(nodes, total, shown, settled);
	if (ranges === null) {
		clearReveal();
		return false;
	}
	registry.set(HIDDEN_HIGHLIGHT, revealHighlight(ranges.hidden));
	registry.set(FADE_NEAR_HIGHLIGHT, revealHighlight(ranges.fadeNear));
	registry.set(FADE_FAR_HIGHLIGHT, revealHighlight(ranges.fadeFar));
	return true;
}
